/**
 * Reine Datumsfelder (Inbetriebnahme, Montage, Geburtsdatum, Erinnerung,
 * Angebotsdatum …) sind am Gerät immer „JJJJ-MM-TT“.
 *
 * Bis 0.17 lieferte der Server sie als Zeitstempel: pg macht aus einem `date`
 * ein JavaScript-Date, und das wurde zu „2026-10-29T23:00:00.000Z“ — Wiener
 * Mitternacht in UTC, also der Vortag. Am Gerät hieß das: Datumsfelder
 * erschienen leer (ein Datumsfeld versteht keinen Zeitstempel), wer
 * hindurchtippte, löschte das Datum, Erinnerungen wurden nie fällig, und auf
 * dem Angebot stand das Rohformat (gefunden beim Durchsehen, 11/2026).
 */

/** Ein Datumswert als „JJJJ-MM-TT“ — ein Zeitstempel wird zum Tag in Ortszeit. */
export function tagVon(wert: unknown): string | null {
  if (typeof wert !== 'string' || wert === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(wert)) return wert;
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** „3.11.2026“ für die Anzeige — aus einem Tag oder einem Zeitstempel. */
export function tagAnzeige(wert: unknown): string {
  const tag = tagVon(wert);
  if (!tag) return '';
  const [j, m, t] = tag.split('-').map(Number);
  return new Date(j!, m! - 1, t!).toLocaleDateString('de-AT');
}

/** Welche Spalten je Tabelle reine Datumsfelder sind (Typ `date` in Postgres). */
export const DATUMSFELDER: Record<string, readonly string[]> = {
  projekte: ['inbetriebnahme', 'kunde_geburtsdatum'],
  auftraege: ['montage_von', 'montage_bis'],
  pruefbefunde: ['geprueft_am'],
  angebote: ['datum', 'gueltig_bis'],
  module: ['preis_stand'],
  komponenten: ['preis_stand'],
  artikel: ['preis_stand'],
  projektschritte: ['erinnern_am'],
};

/** Die Datumsfelder einer Zeile auf „JJJJ-MM-TT“ bringen; andere bleiben, wie sie sind. */
export function mitTagen<T extends Record<string, unknown>>(tabelle: string, zeile: T): T {
  const felder = DATUMSFELDER[tabelle];
  if (!felder) return zeile;
  let neu: T | null = null;
  for (const feld of felder) {
    const alt = zeile[feld];
    if (alt == null) continue;
    const tag = tagVon(alt);
    if (tag !== alt) (neu ??= { ...zeile })[feld as keyof T] = tag as T[keyof T];
  }
  return neu ?? zeile;
}
